import React, { useEffect } from "react";
import { useTranslation } from "react-i18next";

interface ModalProps {
	isOpen: boolean;
	onClose: () => void;
	title?: string;
	children: React.ReactNode;
}

const Modal: React.FC<ModalProps> = ({ isOpen, onClose, title, children }) => {
	const { t } = useTranslation();

	useEffect(() => {
		const handleEscape = (e: KeyboardEvent) => {
			if (e.key === "Escape" && isOpen) {
				onClose();
			}
		};

		document.addEventListener("keydown", handleEscape);
		return () => {
			document.removeEventListener("keydown", handleEscape);
		};
	}, [isOpen, onClose]);

	if (!isOpen) return null;

	return (
		<div
			className="fixed inset-0 z-50 overflow-y-auto"
			onClick={onClose}
			role="dialog"
			aria-modal="true"
			aria-labelledby={title ? "modal-title" : undefined}
		>
			<div className="fixed inset-0 bg-black/60 backdrop-blur-sm transition-opacity"></div>

			<div className="flex min-h-full items-center justify-center p-4">
				<div
					className="relative bg-white rounded-2xl shadow-soft-lg max-w-md w-full border border-gray-100 animate-in fade-in zoom-in duration-200"
					onClick={(e) => e.stopPropagation()}
				>
					{title && (
						<div className="flex items-center justify-between p-6 border-b border-gray-200">
							<h3
								id="modal-title"
								className="text-xl font-bold text-gray-800"
							>
								{title}
							</h3>
							<button
								onClick={onClose}
								className="text-gray-400 hover:text-gray-600 transition-colors"
								aria-label={t("common.closeModal")}
							>
								<svg
									className="w-6 h-6"
									fill="none"
									strokeLinecap="round"
									strokeLinejoin="round"
									strokeWidth="2"
									viewBox="0 0 24 24"
									stroke="currentColor"
								>
									<path d="M6 18L18 6M6 6l12 12"></path>
								</svg>
							</button>
						</div>
					)}

					<div className="p-6">{children}</div>
				</div>
			</div>
		</div>
	);
};

export default Modal;
